import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { useFeatureFlag, useFeatureFlagExposure } from 'featureFlags';
import { useAppSelector } from 'hooks';
import { RecentTable } from 'reducers/user';
import saloonTheme from 'styles/saloonTheme';

import MaintenanceMarquee from 'components/MaintenanceSchedule/MaintenanceMarquee';
import TickerBanner from 'components/TickerBanner/TickerBanner';

import ActivityStrip, { ActivityStripStatus } from './ActivityStrip';
import { deriveBlindLevels, matchesBlinds } from './blindLevels';
import { sortCashStakeGroups, sortCashTables } from './cashStakeOrder';
import { useCompactLobbyHeader, useWideLobbyHeader } from './compactHeader';
import FilterDrawer, { DrawerFilters } from './FilterDrawer';
import FilterFab from './FilterFab';
import { CashGameTile } from './GameTile';
import SaloonHeader from './Header';
import HorizontalScrollRow from './HorizontalScrollRow';
import {
  BulletIcon,
  CardsIcon,
  ChipStackIcon,
  ClockIcon,
  CoinIcon,
  DiamondIcon,
  HeadsUpIcon,
  RocketIcon,
  SixSeatIcon,
  SnowflakeIcon,
  SpadeIcon,
  StarOutlineIcon
} from './icons';
import { useLobbyCardSize } from './lobbyCardSize';
import PromoDialog from './PromoDialog';
import PromoStrip from './PromoStrip';
import { scrollRootOf } from './scrollRoot';
import { SHOW_LOBBY_FORMAT_EVENT, ShowLobbyFormatDetail } from './showLobbyFormat';
import { formatBuyInHeading, groupSitGosByBuyIn, sitGoCardName } from './sitGoBuyInSections';
import SitGoLadder from './SitGoLadder';
import TournamentDayCarousel from './TournamentDayCarousel';
import TypeRow, { ChipDef } from './TypeRow';
import {
  ActiveTable,
  ActivityMetrics,
  CashGame,
  DayMeta,
  PlayerSummary,
  PromoData,
  SitGo,
  Tournament
} from './types';
import { useCollapsingRow } from './useCollapsingRow';
import { useDragScroll } from './useDragScroll';
import { SEATS_BY_FORMAT, SitgoSeats, useLobbyFilters } from './useLobbyFilters';
import { useLobbyView } from './useLobbyView';
import YourTables from './YourTables';

import styles from './SaloonLobby.module.scss';

export type Format = 'cash' | 'sitgo' | 'tournament';

// Stable empty-set default for the optional restriction props so an absent
// prop doesn't create a new Set each render (which would defeat tile memoization).
const EMPTY_ID_SET: Set<string> = new Set();

const FORMAT_TABS: {
  id: Format;
  label: string;
  // The page's subject while this tab is active (EN-10751). The lobby never
  // prints a title — the tab strip is the only thing that says which room you
  // are in — so this is the <h1> a screen reader announces, and it is written
  // out rather than reusing `label`, which is sized to fit a 12px tab.
  heading: string;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
}[] = [
  { id: 'cash', label: 'Cash', heading: 'Cash games', Icon: ChipStackIcon },
  { id: 'sitgo', label: 'Sit & Go', heading: 'Sit & Go', Icon: ClockIcon },
  { id: 'tournament', label: 'Tournaments', heading: 'Tournaments', Icon: StarOutlineIcon }
];

// The footer tab's NEW badge (EN-10748): the introduction modal's tag, worn by
// the tab the introduction points at while its flag is on. Natural case here;
// the stylesheet sets it in capitals.
const NEW_TAB_BADGE = 'New';

// EN-10516 QoL: "1 tables" was rendering wherever a stake had a single game.
const countLabel = (n: number, noun: string, plural = `${noun}s`): string =>
  `${n} ${n === 1 ? noun : plural}`;

const CASH_CHIPS: ChipDef[] = [
  { id: 'holdem', label: "Hold'Em", icon: <SpadeIcon /> },
  { id: 'omaha', label: 'Omaha', icon: <DiamondIcon /> },
  { id: 'hilo', label: 'Hi/Lo', icon: <DiamondIcon /> },
  { id: 'hideFull', label: 'Hide Full', variant: 'dark' },
  // The pair reads as a pair (owner): full tables you cannot join, empty
  // tables you would be starting alone.
  { id: 'hideEmpty', label: 'Hide Empty', variant: 'dark' }
];

// Sit & Go runs Hold'em only — there are no Omaha sit & gos, so Omaha is not
// offered as a filter here (it stays on the Cash tab). Hold'em alone is not a
// filter either, so the tab's pills are its seat toggle instead.
//
// The seat toggle (owner, 2026-09-10; restored by EN-10747 behind the
// `sitgoSeatToggle` flag): two chips that look exactly like the game-type
// pills, each toggled on or off by itself (EN-11104: both on to start, both
// off lists nothing). The choice filters the tab by seat count, so it changes the prize pools in the headings
// and the seats on the tiles at once. Hide Full
// rides along as the cash tab's dark chip: heads-up fills tables far more
// often, and a pool's column of finished games can get long. With the flag off
// the tab has no pills at all (see showFilterPills) — a toggle with one
// reachable side is a control that cannot do anything (owner, 2026-09-12).
// The same holds under `sitGoBuyInLobby` (EN-11139), which changes the tab's
// layout only: the filter is this flag's, whichever layout is on screen.
const SITGO_SEAT_CHIPS: ChipDef[] = [
  { id: 'headsUp', label: 'Heads-up', icon: <HeadsUpIcon /> },
  { id: 'sixSeat', label: 'Six-seat', icon: <SixSeatIcon /> },
  { id: 'hideFull', label: 'Hide Full', variant: 'dark' }
];

/**
 * Which tables a seat chip shows: the seat count it names, and nothing else.
 *
 * "Heads-up is two seats, six-seat is everything bigger" would make the
 * six-seat chip a catch-all. Production only ever runs the two ladders, so on
 * production the two rules agree — but a lower environment carries hand-seeded
 * 3- and 5-player templates, and a catch-all lists a 3-seat table under a
 * heading that says Six-seat (owner, 2026-09-10). Those templates are not a
 * product, and neither chip claims them.
 */
export const matchesSeats = (capacity: number, seats: SitgoSeats): boolean =>
  capacity === SEATS_BY_FORMAT[seats];

// Reentry chip displays as "NLH" to match Unity's label — the tournament
// tile's type pill still shows the raw typeLabel (e.g. "Re-Entry") so the
// filter and the per-tile display intentionally differ.
const TOURNAMENT_CHIPS: ChipDef[] = [
  { id: 'reentry', label: 'NLH', icon: <CardsIcon /> },
  { id: 'bounty', label: 'Bounty', icon: <BulletIcon /> },
  { id: 'freezeout', label: 'Freezeout', icon: <SnowflakeIcon /> },
  { id: 'freeroll', label: 'Freeroll', icon: <CoinIcon /> },
  { id: 'satellite', label: 'Satellite', icon: <RocketIcon /> }
];

/**
 * Map the active tournament filter chips to the set of tournament `type` values
 * that should be shown (see mapTournamentType in SaloonLobbyContainer).
 *
 * The 'reentry' chip is Unity's 'NLH' filter (Standard + Re-Entry under one
 * chip); freerolls are NLH-format tournaments, so they're included there too —
 * in addition to the dedicated 'freeroll' chip. Exported for unit testing.
 */
export const tournamentTypePicks = (activeChips: Set<string>): Set<string> => {
  const picks = new Set<string>();
  if (activeChips.has('reentry')) {
    picks.add('Re-Entry');
    picks.add('Freeroll');
  }
  if (activeChips.has('bounty')) picks.add('Bounty');
  if (activeChips.has('freezeout')) picks.add('Freezeout');
  if (activeChips.has('freeroll')) picks.add('Freeroll');
  if (activeChips.has('satellite')) picks.add('Satellite');
  return picks;
};

/**
 * Compact human-friendly elapsed time since the player last sat at a table.
 * Exported for unit testing — mirrors NetflixLobby/MyRecentTablesRow.tsx.
 */
export const relativeTime = (unixSeconds: number): string => {
  if (!unixSeconds) return '';
  const diff = Math.max(0, Date.now() / 1000 - unixSeconds);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 2) return 'yesterday';
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  });
};

// EN-10516: the lobby used ONE string for two different situations — "you
// filtered too much" was shown even when the source genuinely had no games
// running, telling players to loosen filters that were not the cause. These
// are distinct states and only one of them is the player's doing.
//
// `sourceHasGames` distinguishes them from data already in hand: if the
// unfiltered list has entries, the current filters removed them (over-filtered);
// if it is empty, nothing is running (ready-empty).
//
// Deliberately NOT covered here: `failed` and `stale`. Telling them apart from
// "nothing is running" needs a connection/snapshot signal this component is not
// given, and inventing one would just be a second lie. Threading that signal is
// tracked separately.
export const emptyCopy = (sourceHasGames: boolean): string =>
  sourceHasGames
    ? 'Whoa, partner. You filtered too much. It\u2019s looking mighty empty here.'
    : 'No games running right now. Check back shortly \u2014 tables open up all day.';

interface SaloonLobbyProps {
  player: PlayerSummary;
  activity?: ActivityMetrics;
  // Explicit fetch status for the strip (loading / fresh / stale — see
  // SaloonLobbyContainer). Omitted by callers that don't care about the
  // loading/stale distinction (Storybook, older tests): in that case this
  // falls back to 'fresh' when `activity` is supplied, or stays hidden
  // entirely when it isn't — the same behaviour this prop pair had before
  // the loading/stale states existed.
  activityStatus?: ActivityStripStatus;
  /**
   * Lobby Lab only: sample copy for the maintenance marquee, which is otherwise
   * invisible outside a maintenance window. A real notice wins over it and the
   * lobby's maintenance behaviour is untouched — see MaintenanceMarquee.
   */
  maintenancePreview?: string;
  // Forwarded to ActivityStrip's Retry action (only rendered while
  // activityStatus is 'unavailable'). Optional so callers that don't pass
  // activityStatus either (Storybook, older tests) don't need it — those
  // never reach the 'unavailable' branch that would use it.
  onActivityRetry?: () => void;
  promo?: PromoData;
  cashGames: CashGame[];
  sitGos: SitGo[];
  tournaments: Tournament[];
  tournamentDays: DayMeta[];
  /** Engine clock (epoch seconds) — authoritative "today" for date filtering. */
  serverTime: number;
  activeTables: ActiveTable[];
  recentItems?: Array<{ game: CashGame; recent: RecentTable }>;
  // EN-10064: advisory restriction markers — IDs the viewer can't seat at /
  // register for. Cash tiles use per-game-type sets (texas vs omaha share the
  // same gameId namespace so the split avoids mis-marking the opposite type).
  // SnG tiles render through CashGameTile (keyed by tournamentId); MTT IDs
  // flow to the tournament day carousel.
  restrictedTexasCGIds?: Set<string>;
  restrictedOmahaCGIds?: Set<string>;
  restrictedSngIds?: Set<string>;
  restrictedMttIds?: Set<string>;
  onOpenTable?: (gameID: string, game: string) => boolean | void;
  onOpenTournament?: (id: string) => void;
  // Post-login resume: a guest's "Login/Sign up to Join" (SNG) or "Register"
  // (MTT) click on a tournament stashes the intent and sends them to login;
  // once back, the container re-opens that tournament's detail modal — but
  // this tab defaults to 'cash' on every fresh mount (see the comment below),
  // so without this the modal reopens correctly while the lobby underneath
  // still shows the Cash tab. The container sets this once the resumed
  // tournament's list (tournamentList vs sitAndGoList) tells it which tab is
  // actually correct, then clears it via onForceFormatConsumed.
  forceFormat?: Format | null;
  onForceFormatConsumed?: () => void;
  // Told which tab is on screen, so the container can record a feature flag's
  // exposure on the tab where the player actually meets it (EN-11016).
  onFormatShown?: (format: Format) => void;
  /**
   * Lobby Lab only: tabs to mark NEW as if their flag were on. The real
   * source is the flag (`headsUpIntro` marks Sit & Go while the heads-up
   * introduction is live, EN-10748); this adds to it, never removes.
   */
  newTabsPreview?: readonly Format[];
}

const SaloonLobby: React.FC<SaloonLobbyProps> = ({
  player,
  activity,
  activityStatus,
  onActivityRetry,
  maintenancePreview,
  promo,
  cashGames,
  sitGos,
  tournaments,
  tournamentDays,
  serverTime,
  activeTables,
  recentItems = [],
  restrictedTexasCGIds = EMPTY_ID_SET,
  restrictedOmahaCGIds = EMPTY_ID_SET,
  restrictedSngIds = EMPTY_ID_SET,
  restrictedMttIds = EMPTY_ID_SET,
  onOpenTable,
  onOpenTournament,
  forceFormat,
  onForceFormatConsumed,
  onFormatShown,
  newTabsPreview
}) => {
  // See the `activityStatus` prop comment: callers that don't pass it get the
  // pre-loading/stale behaviour back (fresh-if-present, hidden-if-not).
  const resolvedActivityStatus = activityStatus ?? (activity ? 'fresh' : undefined);

  // During maintenance, cash tables are dimmed and unclickable (players can't
  // open/join a table). Tournament tiles stay openable but their Register is
  // disabled in the detail modal.
  const isMaintenance = useAppSelector(
    (state) => (state as any)?.maintenanceSchedule?.isMaintenance
  );
  const cashTileDim: CSSProperties | undefined = isMaintenance
    ? { opacity: 0.4, pointerEvents: 'none', filter: 'grayscale(0.3)' }
    : undefined;
  const {
    filters: persisted,
    toggleChip,
    setDrawer,
    resetToDefault,
    toggleSitgoSeats,
    showOnlySitgoSeats
  } = useLobbyFilters();
  // EN-10747: off is the tab as it was — no filter row, every seat count shown.
  const sitgoSeatToggle = useFeatureFlag('sitgoSeatToggle');
  // EN-10748: while the heads-up introduction is live (any edition of
  // `release.heads-up-intro`) AND this tab can show Heads-up (the seat toggle
  // on, the introduction's own gate), the Sit & Go tab wears a NEW badge, so a
  // player who dismissed the introduction can still see there is something new
  // on that tab. Off, or once the flag is removed, the badge goes with it.
  const headsUpIntro = useFeatureFlag('headsUpIntro');
  const newTabs = useMemo(() => {
    const tabs = new Set<Format>(newTabsPreview ?? []);
    if (headsUpIntro && sitgoSeatToggle) tabs.add('sitgo');
    return tabs;
  }, [headsUpIntro, sitgoSeatToggle, newTabsPreview]);
  // EN-11139: the Sit & Go tab laid out as the cash tab — a section per buy-in.
  // Layout only: the filter above is the seat toggle's either way; off is the
  // prize-pool ladder.
  const sitGoBuyInLobby = useFeatureFlag('sitGoBuyInLobby');
  // EN-10885: off hides the strip's In Play stat at any amount.
  const cashInPlayEnabled = useFeatureFlag('lobbyCashInPlay');
  // Tile density now lives in Account → Settings → Lobby Settings (shared store),
  // not the Table Filters drawer. Read-only here — the lobby just renders at the
  // chosen size and re-renders live when it's changed in Settings.
  const { cardSize } = useLobbyCardSize();
  // Compact narrows the horizontal-scroll tiles (190px vs the large 280px) so
  // more games sit in view per row — see .gridCompact in SaloonLobby.module.scss.
  const gridClass = cardSize === 'compact' ? `${styles.grid} ${styles.gridCompact}` : styles.grid;
  const playerScope = useAppSelector((state) => state.user?.playerGUID || '');
  const { format, setFormat, scrollRef, tournamentDayRef } = useLobbyView(playerScope);
  // Recorded for both variants whenever the Sit & Go tab is on screen.
  useFeatureFlagExposure('sitGoBuyInLobby', 'sit_go_lobby', format === 'sitgo');

  // Post-login tournament/SNG resume — see the forceFormat prop comment.
  // Runs after mount (the container doesn't know which tab is correct until
  // its own list-arrival effect resolves), so this can't just be an initial
  // useState value.
  useEffect(() => {
    if (!forceFormat) return;
    setFormat(forceFormat);
    onForceFormatConsumed?.();
  }, [forceFormat, onForceFormatConsumed]);
  useEffect(() => {
    onFormatShown?.(format);
  }, [format, onFormatShown]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [promoDialogOpen, setPromoDialogOpen] = useState(false);
  // Where the club stats live follows the header (owner, 2026-09-08): in the
  // wide header they trail the pills inside the one carousel; under the compact
  // header they take a pinned row of their own beneath the pills.
  const compactHeader = useCompactLobbyHeader();
  // Above 1024px the promo ribbon rides in the header row instead of the
  // scroll-away band (owner, 2026-09-09): a centred ribbon and a centred panel
  // split by the full-width bar read as two loose boxes; beside the wordmark
  // the ribbon reads as part of the chrome and the panel stands alone.
  const promoInHeader = useWideLobbyHeader() && !!promo;
  // …and that row collapses on scroll (owner): it hides as the list scrolls
  // down and is the FIRST thing back on the way up, while the bar and the Your
  // Tables panel stay fixed. Two CSS vars on the scroll root, no React state —
  // see useCollapsingRow.
  // The row itself is zero-height (SaloonLobby.module.scss .activityRow); the
  // strip inside it is what collapses, so that is what the hook measures.
  const setActivityRowEl = useCollapsingRow({
    extent: (row) => row.firstElementChild as HTMLElement | null
  });
  // Does the pinned filter bar hold the club stats as well as the pills? Only
  // when the strip is showing AND the header is wide enough to keep them on one
  // row; under the compact header the stats take a row of their own. When this
  // is false the pills are alone in the bar and centre in it (EN-10638).
  // "Showing" is a STATUS, not metrics (EN-10417): the strip renders its own
  // loading / stale / unavailable states, so it is present whenever the
  // container hands it a status, with or without numbers.
  const activityInBar = resolvedActivityStatus !== undefined && !compactHeader;
  // The format pills are the bar's LEADING occupant, and a tab can opt out of
  // them. Sit & Go does while the seat toggle is off (owner, 2026-09-10,
  // restored 2026-09-12): it runs a handful of Hold'em-only tables, so its only
  // chips were "Hold'em" and "Hide Full" — nothing to filter. With the toggle
  // on, its pills are the seat toggle (see SITGO_SEAT_CHIPS) and every tab has
  // a leading occupant again. The drawer (the floating filter button) is
  // separate and unchanged.
  const showFilterPills = format !== 'sitgo' || sitgoSeatToggle;
  // THE BAR'S RULE (owner, 2026-09-10): two possible occupants — pills leading,
  // club stats trailing. Both present → pills lead, stats trail. One present →
  // it centres, whichever it is. None → the bar does not render at all, and the
  // pin chain beneath it closes up (see setFiltersEl). Today the stats are hidden
  // on every tab (EN-10638) and the pills are hidden on Sit & Go while its seat
  // toggle is off, so that tab has no bar; switch the stats back on and they
  // come back centred, or the toggle on and the pills do.
  const barOccupants = Number(showFilterPills) + Number(activityInBar);

  const handleTournamentDayChange = useCallback(
    (dayKey: string) => {
      tournamentDayRef.current = dayKey;
    },
    [tournamentDayRef]
  );

  // Height of the sticky format-filter bar, published as --filters-h so the two
  // sticky layers below it (the "Your Tables" strip, then the tournament day
  // filter) pin flush underneath it rather than behind it. A callback ref plus a
  // ResizeObserver keeps it correct as the chip row wraps or the viewport changes.
  //
  // Header collapse no longer depends on scroll listeners.
  // The ambient header (ticker / activity / promo) is ordinary content inside the
  // scroll container, so the scroller alone carries it away and brings it back.
  // That retired the direction accumulator, the dual collapse thresholds, the
  // post-toggle lock, the overscroll and reflow filters, the per-tab collapsed
  // memory and the measured-height var — all of which existed only to keep a
  // layout animation alive through a real touch gesture.
  // The measured pin vars are published against the scroll container resolved
  // FROM the measured element (see ./scrollRoot for why not the React ref).

  const filtersRoRef = useRef<ResizeObserver | null>(null);
  // The bar scrolls the pills AND the stats as one carousel, so it takes the
  // drag-to-scroll the chip row used to own (TypeRow renders `inline` here).
  const filtersDragRef = useRef<HTMLDivElement | null>(null);
  // The bar is absent on Sit & Go (barOccupants 0) and rebuilt on the way back
  // to Cash or Tournaments, so the one-shot attach would stay bound to the
  // DETACHED bar and the new one would take no drags at all (review,
  // 2026-09-13).
  useDragScroll(filtersDragRef, { reattachKey: barOccupants > 0 });
  const setFiltersEl = useCallback((el: HTMLDivElement | null) => {
    filtersDragRef.current = el;
    filtersRoRef.current?.disconnect();
    // React attaches a PARENT's ref after its children's, so on first mount
    // scrollRef.current is still null here and the synchronous write below is
    // skipped. What covers that is the ResizeObserver's INITIAL delivery: it
    // fires asynchronously once observed, by which time the root exists, so
    // --filters-h and --filters-floor land on the first frame in any real
    // browser. (An environment with no ResizeObserver — jsdom — gets only the
    // synchronous attempt; its tests stub a deferred observer to match.) When
    // the bar UNMOUNTS, `el` is null, the root is already attached, and the
    // synchronous write publishes 0px / 0 directly.
    const apply = () => {
      const root = scrollRootOf(el) ?? scrollRef.current;
      if (!root) return;
      // getBoundingClientRect().height, NOT offsetHeight: offsetHeight is an
      // INTEGER, while the bar's real height is fractional (e.g. 61.6px).
      // Rounding to 62 pins the Your Tables strip BELOW the bar's true bottom
      // edge, opening a hairline slot that scrolling cards show through — the
      // "gap between the rows" the owner kept reporting. Fractional height
      // removes the rounding entirely.
      const h = el ? el.getBoundingClientRect().height : 0;
      root.style.setProperty('--filters-h', h + 'px');
      // The Current Tables panel pins beneath max(measured, DESIGNED) bar
      // height — the designed floor guards against a stale low measurement.
      // That floor only makes sense while a bar exists. When the bar is not
      // rendered at all (Sit & Go with the stats hidden and the seat toggle
      // off), this goes to 0 and the floor with it, so the panel closes up to
      // the header instead of pinning 64px below nothing.
      root.style.setProperty('--filters-floor', el ? '1' : '0');
    };
    if (el && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(apply);
      ro.observe(el);
      filtersRoRef.current = ro;
    } else {
      filtersRoRef.current = null;
    }
    apply();
  }, []);

  useEffect(() => () => filtersRoRef.current?.disconnect(), []);

  // Tapping the tab you are already on returns to the top of the list — the
  // platform-standard gesture, and now the deliberate way to bring the ambient
  // header back without flicking up through the whole list. User-initiated, so it
  // animates unless the player asked for reduced motion.
  const scrollToTop = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
    el.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, []);

  // Measure the sticky "Your Tables" strip so the tournament day chips (and the
  // column heads beneath them) can pin FLUSH beneath it (via the --your-tables-h
  // CSS var). A callback ref keeps this correct as the strip resizes AND as it
  // mounts/unmounts (it renders null when the player has no active/queued
  // tables) — on unmount the offset resets to 0.
  const yourTablesRoRef = useRef<ResizeObserver | null>(null);
  const setYourTablesEl = useCallback((el: HTMLDivElement | null) => {
    yourTablesRoRef.current?.disconnect();
    const apply = () => {
      // Expose two things so the tournament day-filter pins perfectly:
      //  • --your-tables-h: the strip's fractional height → the filter's sticky
      //    `top` (pins FLUSH with the strip's bottom, so no see-through band).
      //    getBoundingClientRect, NOT offsetHeight — the same integer-rounding
      //    defect that opened the bar/strip seam lives here one link down the
      //    chain: an integer height rounds the day filter a fraction of a pixel
      //    low and lets tournament cards show through behind it.
      //  • --your-tables-gap: minus the strip's bottom margin. The day chips used
      //    to cancel it in flow; they now pin at the panel's bottom edge, which
      //    has no margin, so nothing in the lobby reads it any more — the
      //    Storybook diagnostics still show it. Both reset to 0 when the strip is
      //    absent (no active/queued tables), keeping the chips flush at the
      //    scroll top with no phantom offset.
      let h = 0;
      let gap = 0;
      if (el) {
        h = el.getBoundingClientRect().height;
        gap = parseFloat(getComputedStyle(el).marginBottom) || 0;
      }
      const root = scrollRootOf(el) ?? scrollRef.current;
      if (!root) return;
      root.style.setProperty('--your-tables-h', `${h}px`);
      root.style.setProperty('--your-tables-gap', `-${gap}px`);
    };
    if (el && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(apply);
      ro.observe(el);
      yourTablesRoRef.current = ro;
    } else {
      yourTablesRoRef.current = null;
    }
    apply();
  }, []);

  // Today, on the ENGINE's clock, in the dayKey shape the carousel groups by —
  // a device with a wrong date must not mark the wrong column. It does recompute
  // on every server-time tick — a string concat, so the memo is about identity
  // for the consumers below, not about the cost.
  //
  // `undefined` until the engine has actually spoken. Falling back to the
  // device clock here would mark a column on exactly the machines this memo
  // exists to protect against, for as long as the first tick takes to arrive
  // — and the carousel is already built for the absence: `todayKey` is
  // optional and it marks nothing rather than guessing (review, 2026-09-13).
  const todayKey = useMemo(() => {
    // `!Number.isFinite`, not `<= 0`: `NaN <= 0` is FALSE, so a non-finite
    // clock fell straight through to `new Date(NaN)` and produced the string
    // 'NaN-NaN-NaN' — which sorts ABOVE every real day key ('2' is 0x32, 'N'
    // is 0x4E), and the day check below runs before the isRegistered
    // pass-through, so the tab emptied completely, the player's own
    // tournaments included, with no error. One `||` away from reachable
    // (round 2, 2026-09-13). Infinity yields an Invalid Date too.
    if (!Number.isFinite(serverTime) || serverTime <= 0) return undefined;
    const now = new Date(serverTime * 1000);
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
      now.getDate()
    ).padStart(2, '0')}`;
  }, [serverTime]);

  const handleFormatChange = setFormat;

  // Another surface can ask for a tab (showLobbyFormat), the way the cashier
  // asks for the profile panel: the multi-run intro's BROWSE TABLES lands on
  // Cash (EN-10791). It goes through the tab handler so scroll is kept as usual.
  // A request for Sit & Go can set the seat toggle as well: the heads-up
  // introduction's FIND A HEADS-UP TABLE lands with Heads-up lit, so six-seat
  // games are out of the way (EN-10748). The choice is written to the toggle's
  // own store, where the tab's pills and the drawer read it while the seat
  // toggle flag is on; with it off there are no pills, and nothing reads it.
  useEffect(() => {
    const onShowFormat = (event: Event) => {
      const detail = (event as CustomEvent<Partial<ShowLobbyFormatDetail>>).detail;
      const next = detail?.format;
      if (next !== 'cash' && next !== 'sitgo' && next !== 'tournament') return;
      const seats = detail?.sitgoSeats;
      if (next === 'sitgo' && (seats === 'headsUp' || seats === 'sixSeat')) {
        showOnlySitgoSeats(seats);
      }
      handleFormatChange(next);
    };
    window.addEventListener(SHOW_LOBBY_FORMAT_EVENT, onShowFormat);
    return () => window.removeEventListener(SHOW_LOBBY_FORMAT_EVENT, onShowFormat);
  }, [handleFormatChange, showOnlySitgoSeats]);

  const activeChips = useMemo(
    () => new Set(persisted.chipSelections[format]),
    [persisted.chipSelections, format]
  );
  // The pills show the game-type selection — except on the Sit & Go ladder with
  // the seat toggle on, where they are the toggle (each format lit while it is
  // on) plus the Hide Full chip, which toggles like everywhere else.
  const seatToggle = format === 'sitgo' && sitgoSeatToggle;
  const pillSelection = useMemo(() => {
    if (!seatToggle) return activeChips;
    const lit = new Set<string>(persisted.sitgoSeats);
    if (activeChips.has('hideFull')) lit.add('hideFull');
    return lit;
  }, [seatToggle, persisted.sitgoSeats, activeChips]);

  // Set of IDs that should show the active orangy-red glow on CashGameTile.
  // Two sources contribute:
  //   • activeTables (cash games the player is currently sitting at)
  //   • SnGs the player is registered for (the SnG tile renders through
  //     CashGameTile, so its glow is driven by the same set)
  // Tournament tiles handle their own active state internally in
  // TournamentTile via tournament.isRegistered + status === 'P'.
  const playerGameIds = useMemo(() => {
    // Only highlight tables where the player is actually seated — not tables
    // they are merely observing or have navigated to but not yet joined.
    // state.tables includes observed/pending tables too, so without this
    // filter the tile glows the moment you click it, before buying in.
    const set = new Set<string>(activeTables.filter((t) => t.isSeated).map((t) => t.gameID));
    for (const s of sitGos) {
      if (s.isRegistered) set.add(s.id);
    }
    return set;
  }, [activeTables, sitGos]);

  // Strict subset of playerGameIds: only tables the player actually SITS at
  // (activeTables carries observed-but-not-seated tables too). The Join
  // Waitlist banner is gated on this — observing a full table must not hide
  // the banner, otherwise leaving the waitlist while still viewing the table
  // leaves no way to rejoin from the lobby.
  const seatedGameIds = useMemo(() => {
    const set = new Set<string>(activeTables.filter((t) => t.isSeated).map((t) => t.gameID));
    for (const s of sitGos) {
      if (s.isRegistered) set.add(s.id);
    }
    return set;
  }, [activeTables, sitGos]);

  // The drawer shows and edits the open tab's own picks: each tab keeps its
  // own, so nothing picked on Cash filters Sit & Go, or the other way round.
  // Each list below filters by its own tab's picks, never the open tab's.
  const { cash: cashDrawer, sitgo: sitgoDrawer } = persisted.drawerByFormat;
  const drawerPicks = persisted.drawerByFormat[format];
  const drawerFilters: DrawerFilters = useMemo(
    () => ({
      blinds: new Set(drawerPicks.blinds),
      sizes: new Set(drawerPicks.sizes)
    }),
    [drawerPicks]
  );

  const setDrawerFilters = useCallback(
    (next: DrawerFilters) =>
      setDrawer(format, { blinds: Array.from(next.blinds), sizes: Array.from(next.sizes) }),
    [setDrawer, format]
  );

  const handleChipToggle = useCallback(
    (id: string) => toggleChip(format, id),
    [toggleChip, format]
  );
  // A pill tap: a seat chip toggles its format in the seat toggle's own store
  // (not the chip list — see useLobbyFilters); Hide Full and every other tab's
  // chips toggle as chips.
  const handlePillToggle = useCallback(
    (id: string) => {
      if (seatToggle && (id === 'headsUp' || id === 'sixSeat')) return toggleSitgoSeats(id);
      toggleChip(format, id);
    },
    [seatToggle, toggleChip, format, toggleSitgoSeats]
  );
  const handleClearToDefault = useCallback(() => resetToDefault(format), [resetToDefault, format]);
  // The drawer's Table Size on Sit & Go is the same toggle as the tab's pills:
  // one stored choice, so a tap in either place lights both.
  const drawerSeatToggle = useMemo(
    () =>
      sitgoSeatToggle ? { selected: persisted.sitgoSeats, onToggle: toggleSitgoSeats } : undefined,
    [sitgoSeatToggle, persisted.sitgoSeats, toggleSitgoSeats]
  );

  // Size chip IDs map to capacity ranges: heads-up = 2 seats, 3-6 = 3..6, 9-max = 7..9.
  const matchesSize = (capacity: number, sizes: Set<string>): boolean => {
    if (sizes.size === 0) return true;
    if (sizes.has('heads-up') && capacity <= 2) return true;
    if (sizes.has('3-6') && capacity >= 3 && capacity <= 6) return true;
    if (sizes.has('9-max') && capacity >= 7) return true;
    return false;
  };

  const filteredCash = useMemo(() => {
    const variantPicks = new Set<string>();
    if (activeChips.has('holdem')) variantPicks.add('NLH');
    if (activeChips.has('omaha')) variantPicks.add('PLO');
    if (activeChips.has('hilo')) variantPicks.add('PLO-HiLo');

    // Deselecting every game-type chip yields an empty result rather than
    // falling back to "all games" — matches the tournament tab's behavior
    // and the saloon "no results" copy.
    if (variantPicks.size === 0) return [];

    const hideFull = activeChips.has('hideFull');
    const hideEmpty = activeChips.has('hideEmpty');
    // Cash's own drawer picks, whichever tab the drawer was last opened on.
    const blinds = new Set(cashDrawer.blinds);
    const sizes = new Set(cashDrawer.sizes);

    return cashGames.filter((g) => {
      if (!variantPicks.has(g.variant)) return false;
      if (hideFull && g.seated >= g.capacity) return false;
      if (hideEmpty && g.seated === 0) return false;
      if (!matchesBlinds(g.sb, blinds)) return false;
      if (!matchesSize(g.capacity, sizes)) return false;
      return true;
    });
  }, [activeChips, cashDrawer, cashGames]);

  // Blind-level filter options reflect the cash tables actually in the lobby, so
  // every available stake (including blinds not in the static design grid) is
  // listed. Derived from the full cashGames list — NOT filteredCash — so picking
  // one blind doesn't drop the rest of the options.
  const blindLevels = useMemo(() => deriveBlindLevels(cashGames), [cashGames]);

  const filteredSitgos = useMemo(() => {
    const variantPicks = new Set<string>();
    if (activeChips.has('holdem')) variantPicks.add('NLH');
    if (activeChips.has('omaha')) variantPicks.add('PLO');

    if (variantPicks.size === 0) return [];

    // Hide Full is the cash tab's chip and is persisted per tab, so a stored
    // one is honoured here even while the seat toggle is off and there is no
    // chip on screen to clear it — rather than silently ignored.
    const hideFull = activeChips.has('hideFull');
    // With the toggle off there is no seat filter: every sit & go, grouped by
    // prize pool. Production runs six-seat tables only, so that is production's
    // ladder; a lower environment's hand-seeded 2-, 3- and 5-player templates
    // show up as their own pools until they are reseeded (EN-10731). The
    // drawer's Table Size is one control or the other, and so is this filter:
    // with the toggle on, the seat counts of the formats that are on
    // (matchesSeats), none when both are off; off, Sit & Go's own drawer
    // sizes. A size picked here while the flag was off stays stored but has no
    // button on screen once it is on, so it must not apply then — it would
    // empty the tab with nothing to clear it. sitGoBuyInLobby (EN-11139)
    // changes none of this: its sections are built from this list, so the
    // toggle filters them too.
    const seats = persisted.sitgoSeats;
    const sizes = new Set(sitgoDrawer.sizes);
    const fitsTableSize = (capacity: number) =>
      sitgoSeatToggle ? seats.some((s) => matchesSeats(capacity, s)) : matchesSize(capacity, sizes);

    return sitGos.filter((s) => {
      if (!variantPicks.has(s.variant)) return false;
      if (hideFull && s.registered >= s.capacity) return false;
      return fitsTableSize(s.capacity);
    });
  }, [activeChips, sitgoDrawer, sitGos, sitgoSeatToggle, persisted.sitgoSeats]);

  const filteredTournaments = useMemo(() => {
    const typePicks = tournamentTypePicks(activeChips);

    // Match Unity's LobbiesModel: when no chip is selected the visible list
    // collapses to ONLY the tournaments the player is registered for. They
    // also pass through regardless of filter state when chips ARE active.
    return tournaments.filter((t) => {
      // Hide tournaments whose scheduled day is before today, unless the
      // tournament is still in progress (long-running MTTs spanning days).
      // `todayKey` is the memo above — one "what day is it on the engine's
      // clock" rule for the whole file; this block used to carry a second,
      // character-identical copy that SHADOWED it (review, 2026-09-13). Until
      // the engine has spoken there is no today, so nothing is hidden: showing
      // yesterday's tournament for a moment beats hiding a live one because a
      // device clock ran fast.
      if (todayKey && t.dayKey && t.dayKey < todayKey && t.status !== 'P') return false;
      if (t.isRegistered) return true;
      if (typePicks.size === 0) return false;
      return typePicks.has(t.type);
    });
  }, [activeChips, tournaments, todayKey]);

  const cashByStake = useMemo(() => {
    const map = new Map<string, typeof filteredCash>();
    for (const g of filteredCash) {
      const list = map.get(g.stakes) ?? [];
      list.push(g);
      map.set(g.stakes, list);
    }
    return sortCashStakeGroups(Array.from(map.entries())).map(
      ([stake, tables]) => [stake, sortCashTables(tables)] as const
    );
  }, [filteredCash]);

  // SNGs are segregated by prize pool (heading "$30 prize pool"), mirroring the
  // cash grouping by stakes. Within a group the player's own tables sort first;
  // then (owner, 2026-09-10) the table a player can still join leads — the most
  // visible slot goes to what is next up — and the full ones trail so a player
  // can see the pool has been filling; among open tables the fullest, closest
  // to starting, comes first.
  const sitGosByPrize = useMemo(() => {
    const map = new Map<number, SitGo[]>();
    for (const s of filteredSitgos) {
      const list = map.get(s.prizePool) ?? [];
      list.push(s);
      map.set(s.prizePool, list);
    }
    const sortSitgos = (games: SitGo[]) =>
      [...games].sort((a, b) => {
        const aSub = playerGameIds.has(a.id) ? 1 : 0;
        const bSub = playerGameIds.has(b.id) ? 1 : 0;
        if (bSub !== aSub) return bSub - aSub;
        const aOpen = a.registered < a.capacity ? 1 : 0;
        const bOpen = b.registered < b.capacity ? 1 : 0;
        if (bOpen !== aOpen) return bOpen - aOpen;
        return b.registered - a.registered;
      });
    return Array.from(map.entries())
      .sort(([a], [b]) => a - b)
      .map(([prize, games]) => [prize, sortSitgos(games)] as const);
  }, [filteredSitgos, playerGameIds]);

  // EN-11139 (sitGoBuyInLobby): a section per buy-in, ordered and trimmed to
  // one empty table per template and format (sitGoBuyInSections), matching
  // the server's per-template cleanup. Unused with it off.
  const sitGosByBuyIn = useMemo(
    () => (sitGoBuyInLobby ? groupSitGosByBuyIn(filteredSitgos) : []),
    [sitGoBuyInLobby, filteredSitgos]
  );

  // Sit & Go's chips are the seat toggle; with the toggle off the tab renders
  // no pills at all (showFilterPills), so the set is never shown there.
  const currentChips =
    format === 'cash' ? CASH_CHIPS : format === 'sitgo' ? SITGO_SEAT_CHIPS : TOURNAMENT_CHIPS;

  const handleCashClick = (game: CashGame) => {
    if (!onOpenTable || isMaintenance) return; // cash tables are frozen during maintenance
    const engineGame = game.game || (game.variant === 'NLH' ? 'texas' : 'omaha');
    onOpenTable(game.id, engineGame);
  };
  // SnGs route through the same detail modal as MTTs (the modal varies its
  // layout for SitNGo). Going straight to the table skipped register/observe
  // gating that the old lobby preserved.
  const handleSitGoClick = (game: SitGo) => {
    if (!onOpenTournament) return;
    onOpenTournament(game.id);
  };
  // One Sit & Go tile, shared by the row layout and the ladder, so the two
  // layouts under comparison can never drift in what a tile is told.
  const renderSitGoTile = (s: SitGo) => (
    <CashGameTile
      key={s.id}
      game={{
        id: s.id,
        tableId: s.tableId,
        variant: s.variant,
        banner: s.banner,
        stakes: s.label,
        sb: 0,
        buyInMin: s.buyIn,
        buyInMax: s.buyIn,
        seated: s.registered,
        capacity: s.capacity
      }}
      // EN-11139 AC 5: the format in the name slot, with sitGoBuyInLobby on.
      tournamentName={sitGoBuyInLobby ? sitGoCardName(s) : s.name}
      onClick={() => handleSitGoClick(s)}
      playerGameIds={playerGameIds}
      size={cardSize}
      restricted={restrictedSngIds.has(s.id)}
      restrictedScope='tournament'
    />
  );

  return (
    <ThemeProvider theme={saloonTheme}>
      <div className={styles.scene}>
        {/* Balance bar + icons stay pinned above the scroll area. */}
        <SaloonHeader
          cashBalance={player.cashBalance}
          promoBalance={player.promoBalance}
          avatar={player.avatar}
          // The wordmark is home: the Cash tab, or the top of it when that is
          // already the tab — the same rule the footer tabs follow, so the two
          // ways of reaching a tab behave alike (owner, 2026-09-11).
          onHomeClick={() => (format === 'cash' ? scrollToTop() : handleFormatChange('cash'))}
          promoSlot={
            promoInHeader && promo ? (
              <PromoStrip promo={promo} inHeader onClick={() => setPromoDialogOpen(true)} />
            ) : undefined
          }
        />

        {/* Maintenance message as a scrolling marquee (not a popup). Deliberately
            PINNED rather than scrolled away with the rest of the ambient header: it
            renders only while a maintenance notice / window is active, so it costs
            no space the rest of the time, and it is the one item here a player must
            not be able to scroll past and miss. */}
        <MaintenanceMarquee previewMessage={maintenancePreview} />

        {/* The lobby's single scroll owner. Scroll memory records its position;
            the ambient header below is ordinary content, so the scroller itself
            carries it away and brings it back. */}
        <main className={styles.scroll} ref={scrollRef}>
          {/* The lobby's one <h1> (EN-10751): which room you are in. Invisible —
              the tab strip says it on screen — but it is the root every section
              heading below hangs from, so the outline starts somewhere. */}
          <h1 className={styles.srOnly}>
            {FORMAT_TABS.find((t) => t.id === format)?.heading ?? 'Lobby'}
          </h1>
          {/* Ambient header — ticker / activity stats / promo. Scrolls away with the
              list and returns at the top. Full-bleed (outside .content) so it keeps
              the edge-to-edge geometry it had when it sat above the scroll area. */}
          <div className={styles.scrollAwayHeader}>
            <TickerBanner context='lobby' variant='outlawSolid' />

            {/* The Activity Strip does not ride here (main's seat for it, EN-10417):
                this lobby puts the club stats in the filter bar under the wide
                header and in their own collapsing row under the compact one —
                see activityInBar and .activityRow below. */}
            {promo && !promoInHeader && (
              <PromoStrip promo={promo} onClick={() => setPromoDialogOpen(true)} />
            )}
          </div>

          {/* Format-filter chips pin at the top of the scroll area once the ambient
              header above has scrolled past, so re-filtering never needs a scroll
              back up. Its measured height (--filters-h) is what the "Your Tables"
              strip and the tournament day filter stack beneath. */}
          {/* EN-10516 (owner): club metrics share the pinned utility bar with the
              format chips — one row on desktop, stacked on mobile — instead of
              spending a separate scroll-away band. --filters-h measures the
              combined bar, so the pin chain adapts on its own. */}
          {/* The bar renders only with something in it (see barOccupants) and
              centres a lone occupant, whichever it is. Auto margins do the
              centring — never justify-content, which would clip an overflowing
              row's leading edge on this scroller. Reinstating the stats or the
              pills re-populates the bar and the solo class stops applying by
              itself; with neither, there is no bar and the pin chain beneath
              closes up to the header. */}
          {barOccupants > 0 && (
            <div
              className={`${styles.stickyFilters} ${
                barOccupants === 1 ? styles.stickyFiltersSolo : ''
              }`}
              ref={setFiltersEl}>
              {/* Pills lead when both occupants are present — the controls a
                  player acts on sit where reading starts; ambient numbers keep
                  to the right edge. Sit & Go's pills are its seat toggle, and
                  it opts out of pills entirely while that is off. */}
              {showFilterPills && (
                <TypeRow
                  chips={currentChips}
                  selected={pillSelection}
                  onToggle={handlePillToggle}
                  inline
                  className={styles.barPills}
                />
              )}
              {/* EN-10417 (#3894): the strip reads the CDN activity document and
                  carries its own loading / stale / unavailable states, so the
                  CONTAINER decides whether it shows by handing it a status —
                  ACTIVITY_STRIP_ENABLED in SaloonLobbyContainer.tsx is the one
                  place to hide it again. */}
              {activityInBar && resolvedActivityStatus && (
                <ActivityStrip
                  className={styles.barStats}
                  metrics={activity}
                  status={resolvedActivityStatus}
                  onRetry={onActivityRetry}
                  cashInPlayEnabled={cashInPlayEnabled}
                />
              )}
            </div>
          )}

          {/* COMPACT HEADER (owner, 2026-09-08): the stats leave the carousel —
              where a phone only saw them after scrolling the pills off — and
              take a row of their own, pinned under the bar and above Your Tables.
              Its own sticky layer so the bar's measured height (--filters-h)
              and the pin chain below it stay exactly what they were. The row
              collapses on scroll down and returns first on scroll up
              (useCollapsingRow); every layer pinned beneath translates by the
              same variable and rides up to meet the bar as the stats go. */}
          {resolvedActivityStatus && compactHeader && (
            <div className={styles.activityRow} data-testid='activity-row' ref={setActivityRowEl}>
              <ActivityStrip
                className={styles.rowStats}
                metrics={activity}
                status={resolvedActivityStatus}
                onRetry={onActivityRetry}
                cashInPlayEnabled={cashInPlayEnabled}
              />
            </div>
          )}

          <div className={styles.content}>
            {/* Owner (2026-09-08): the player's tables are a ROW here at every
                width — the production panel, resized by the viewport — not a
                header dock that would have to change shape on phones. */}
            <YourTables
              tables={activeTables}
              onOpenTable={onOpenTable}
              rootRef={setYourTablesEl}
              className={styles.yourTablesPanel}
            />

            {format === 'cash' && recentItems.length > 0 && (
              <section className={styles.group}>
                <div className={styles.groupHeader}>
                  <span className={styles.groupRule} />
                  <h2 className={styles.groupLabel}>Previous Tables</h2>
                  <span className={styles.groupCount}>
                    {countLabel(recentItems.length, 'table')}
                  </span>
                  <span className={styles.groupRule} />
                </div>
                <HorizontalScrollRow className={gridClass}>
                  {recentItems.map(({ game, recent }) => (
                    <div
                      key={`${recent.gameID}-${recent.time}`}
                      className={styles.recentTileWrap}
                      style={cashTileDim}>
                      {recent.time > 0 && (
                        <span className={styles.timeBadge}>{relativeTime(recent.time)}</span>
                      )}
                      <CashGameTile
                        game={game}
                        onClick={() =>
                          isMaintenance ? undefined : onOpenTable?.(game.id, game.game ?? 'texas')
                        }
                        playerGameIds={playerGameIds}
                        seatedGameIds={seatedGameIds}
                        waitlistEnabled
                        size={cardSize}
                        restricted={(game.variant === 'NLH'
                          ? restrictedTexasCGIds
                          : restrictedOmahaCGIds
                        ).has(game.id)}
                      />
                    </div>
                  ))}
                </HorizontalScrollRow>
              </section>
            )}

            {format === 'cash' && (
              <>
                {cashByStake.length === 0 ? (
                  <p className={styles.empty}>{emptyCopy(cashGames.length > 0)}</p>
                ) : (
                  cashByStake.map(([stake, games]) => (
                    <section key={stake} className={styles.group}>
                      <div className={styles.groupHeader}>
                        <span className={styles.groupRule} />
                        <h2 className={styles.groupLabel}>{stake}</h2>
                        <span className={styles.groupCount}>
                          {countLabel(games.length, 'table')}
                        </span>
                        <span className={styles.groupRule} />
                      </div>
                      <HorizontalScrollRow className={gridClass}>
                        {games.map((g) => (
                          <div key={g.id} style={cashTileDim}>
                            <CashGameTile
                              game={g}
                              onClick={() => handleCashClick(g)}
                              playerGameIds={playerGameIds}
                              seatedGameIds={seatedGameIds}
                              waitlistEnabled
                              size={cardSize}
                              restricted={(g.variant === 'NLH'
                                ? restrictedTexasCGIds
                                : restrictedOmahaCGIds
                              ).has(g.id)}
                            />
                          </div>
                        ))}
                      </HorizontalScrollRow>
                    </section>
                  ))
                )}
              </>
            )}

            {format === 'sitgo' &&
              (filteredSitgos.length === 0 ? (
                <section className={styles.group}>
                  <div className={styles.groupHeader}>
                    <span className={styles.groupRule} />
                    <h2 className={styles.groupLabel}>Sit &amp; Go</h2>
                    <span className={styles.groupRule} />
                  </div>
                  <p className={styles.empty}>{emptyCopy(sitGos.length > 0)}</p>
                </section>
              ) : sitGoBuyInLobby ? (
                // EN-11139 AC 2: the cash tab's sections, headed by the buy-in
                // instead of the blinds.
                sitGosByBuyIn.map(([buyIn, games]) => (
                  <section key={buyIn} className={styles.group}>
                    <div className={styles.groupHeader}>
                      <span className={styles.groupRule} />
                      <h2 className={styles.groupLabel}>{formatBuyInHeading(buyIn)}</h2>
                      <span className={styles.groupCount}>{countLabel(games.length, 'table')}</span>
                      <span className={styles.groupRule} />
                    </div>
                    <HorizontalScrollRow className={gridClass}>
                      {games.map(renderSitGoTile)}
                    </HorizontalScrollRow>
                  </section>
                ))
              ) : (
                // One scroller, a column per prize pool, its tables stacked
                // inside (SitGoLadder/). `.group` gives it the section gap from
                // the chrome above, as the first cash stake row gets.
                <section className={styles.group}>
                  <SitGoLadder rungs={sitGosByPrize} size={cardSize} renderTile={renderSitGoTile} />
                </section>
              ))}

            {format === 'tournament' && (
              <TournamentDayCarousel
                days={tournamentDays}
                tournaments={filteredTournaments}
                onSelectTournament={onOpenTournament}
                emptyCopy={emptyCopy(tournaments.length > 0)}
                restrictedIds={restrictedMttIds}
                size={cardSize}
                todayKey={todayKey}
                initialDay={tournamentDayRef.current}
                onDayChange={handleTournamentDayChange}
              />
            )}
          </div>
        </main>

        <FilterFab onClick={() => setDrawerOpen(true)} />

        <nav className={styles.footer} aria-label='Lobby'>
          <div className={styles.footerInner}>
            {FORMAT_TABS.map((tab) => (
              <button
                type='button'
                key={tab.id}
                className={`${styles.footerTab} ${format === tab.id ? styles.footerTabActive : ''}`}
                // The active tab was signalled by colour alone (EN-10751): three
                // identically-named buttons, no way to hear which room you are in.
                aria-current={format === tab.id ? 'page' : undefined}
                // Tapping the tab you are already on scrolls back to the top,
                // matching the platform convention — and it is the deliberate way
                // to bring the ambient header back from deep in a list.
                onClick={() => (format === tab.id ? scrollToTop() : handleFormatChange(tab.id))}>
                <tab.Icon className={styles.footerTabIcon} />
                <span className={styles.footerTabLabel}>{tab.label}</span>
                {/* Last in the DOM so the name reads "Sit & Go, new"; placed at
                    the icon's corner by the stylesheet, out of the layout. */}
                {newTabs.has(tab.id) && (
                  <span className={styles.footerTabNew}>
                    <span className={styles.srOnly}>, </span>
                    {NEW_TAB_BADGE}
                  </span>
                )}
              </button>
            ))}
          </div>
        </nav>

        <FilterDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          format={format}
          onFormatChange={handleFormatChange}
          filters={drawerFilters}
          onChange={setDrawerFilters}
          blindLevels={blindLevels}
          selectedGames={activeChips}
          hideFull={activeChips.has('hideFull')}
          onToggleGame={handleChipToggle}
          onToggleHideFull={() => handleChipToggle('hideFull')}
          seatToggle={drawerSeatToggle}
          onClearToDefault={handleClearToDefault}
        />

        <PromoDialog open={promoDialogOpen} onClose={() => setPromoDialogOpen(false)} />
      </div>
    </ThemeProvider>
  );
};

export default SaloonLobby;
